// Overview traffic KPI strip (tiles only). The click-flow detail — Received/Sent
// cards, the click balance, and the sale filter impact — lives on the "Clicks &
// orders" tab (ClicksOrdersTab.js).
import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  TouchApp,
  TrendingUp,
  PriceCheck,
  Receipt,
} from '@mui/icons-material';
import { dashboardAPI } from '../../services/dashboard-api';
import { formatCurrency } from '../../utils/formatters';
import StatCard from './StatCard';

const TrafficPerformance = ({ startDate, endDate, myRetailerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadTraffic = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await dashboardAPI.getTrafficStats({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        my_retailer_id: myRetailerId || undefined,
      });
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setData(null);
        setError(true);
      }
    } catch {
      setData(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, myRetailerId]);

  useEffect(() => {
    loadTraffic();
  }, [loadTraffic]);

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Traffic performance</Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" height={40} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Traffic performance</Typography>
        <Alert severity="info" variant="outlined">
          Traffic data is currently unavailable.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Traffic performance</Typography>
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Clicks from partners"
            value={data.clicks_from_partners?.toLocaleString() || 0}
            icon={<TouchApp />}
            color="primary.main"
            tooltip="Clicks from partner shops to yours in the selected period — counted from every handoff click, the same counting your click balance uses."
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Conversion rate"
            value={`${data.conversion_rate || 0}%`}
            icon={<TrendingUp />}
            color="success.main"
            tooltip="Percentage of clicks from partners that resulted in an order. Orders acquired divided by clicks from partners."
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Cost per click"
            value={formatCurrency(data.cpc)}
            icon={<PriceCheck />}
            color="warning.main"
            tooltip="Your net acquisition cost divided by clicks from partners. Compare this to your CPC on other channels."
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Average order value"
            value={formatCurrency(data.aov)}
            icon={<Receipt />}
            color="info.main"
            tooltip="Average revenue per order acquired through Handoff."
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrafficPerformance;
